/**
 * Utilities for managing fiscal info modal progress in SessionStorage
 * Stores generated documents and acceptance progress to prevent data loss
 */

const FISCAL_PROGRESS_KEY = 'fiscal_info_progress'

/**
 * Clear all fiscal progress from SessionStorage
 */
const clearFiscalProgress = () => {
  try {
    sessionStorage.removeItem(FISCAL_PROGRESS_KEY)
  } catch (error) {
    // Silent error handling for storage operations
  }
}

/**
 * Structure of stored progress:
 * {
 *   documents: {
 *     'service-contract': 'url',
 *     'beneficiary-declaration': 'url',
 *     'business-strategy-contract': 'url'
 *   },
 *   acceptedDocuments: ['service-contract', 'beneficiary-declaration'],
 *   currentDocumentIndex: 1,
 *   formData: {
 *     rfc: 'ABC123456789',
 *     curp: 'ABCD123456HDFGHI01',
 *     nationality: 'MX',
 *     notificationAddress: 'Address...',
 *     beneficiaries: [...]
 *   },
 *   timestamp: 1698765432000
 * }
 */

/**
 * Save fiscal progress to SessionStorage
 * @param {Object} progress - Progress data to save
 */
export const saveFiscalProgress = (progress) => {
  try {
    const progressData = {
      ...progress,
      timestamp: Date.now()
    }
    
    sessionStorage.setItem(FISCAL_PROGRESS_KEY, JSON.stringify(progressData))
    // Progress saved successfully
  } catch (error) {
    // Error saving progress - silent handling
  }
}

/**
 * Load fiscal progress from SessionStorage
 * @returns {Object|null} - Stored progress data or null if not found
 */
export const loadFiscalProgress = () => {
  try {
    const storedData = sessionStorage.getItem(FISCAL_PROGRESS_KEY)
    
    if (!storedData) {
      return null
    }
    
    const progress = JSON.parse(storedData)
    
    // Validate that the stored data has the expected structure
    if (!progress || typeof progress !== 'object') {
      // Invalid progress data found
      clearFiscalProgress()
      return null
    }
    
    // Check if data is not too old (24 hours max)
    const MAX_AGE = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
    const now = Date.now()
    
    if (progress.timestamp && (now - progress.timestamp) > MAX_AGE) {
      // Progress data is too old, clearing it
      clearFiscalProgress()
      return null
    }
    
    // Progress loaded successfully
    return progress
  } catch (error) {
    // Error loading progress - silent handling
    clearFiscalProgress()
    return null
  }
}

/**
 * Update specific parts of the fiscal progress
 * @param {Object} updates - Partial updates to apply
 */
export const updateFiscalProgress = (updates) => {
  try {
    const currentProgress = loadFiscalProgress() || {}
    const updatedProgress = {
      ...currentProgress,
      ...updates,
      timestamp: Date.now()
    }
    
    saveFiscalProgress(updatedProgress)
  } catch (error) {
    // Error updating progress - silent handling
  }
}

/**
 * Save generated documents to progress
 * @param {Object} documents - Documents object with keys and URLs
 */
export const saveGeneratedDocuments = (documents) => {
  updateFiscalProgress({ documents })
}

/**
 * Save form data to progress
 * @param {Object} formData - Form data to persist
 */
export const saveFormData = (formData) => {
  updateFiscalProgress({ formData })
}

/**
 * Add an accepted document to the progress
 * @param {string} documentKey - Key of the accepted document
 * @param {number} currentIndex - Current document index
 */
export const addAcceptedDocument = (documentKey, currentIndex) => {
  const progress = loadFiscalProgress() || {}
  const acceptedDocuments = progress.acceptedDocuments || []
  
  // Avoid duplicates
  if (!acceptedDocuments.includes(documentKey)) {
    acceptedDocuments.push(documentKey)
  }
  
  updateFiscalProgress({
    acceptedDocuments,
    currentDocumentIndex: currentIndex
  })
}

/**
 * Check if there are generated documents in storage
 * @returns {boolean} - True if documents exist
 */
export const hasGeneratedDocuments = () => {
  const progress = loadFiscalProgress()
  return progress && progress.documents && Object.keys(progress.documents).length > 0
}

/**
 * Check if there are accepted documents in storage
 * @returns {boolean} - True if any documents have been accepted
 */
export const hasAcceptedDocuments = () => {
  const progress = loadFiscalProgress()
  return progress && progress.acceptedDocuments && progress.acceptedDocuments.length > 0
}

/**
 * Get the list of accepted document keys
 * @returns {Array} - Array of accepted document keys
 */
export const getAcceptedDocuments = () => {
  const progress = loadFiscalProgress()
  return (progress && progress.acceptedDocuments) || []
}

/**
 * Get the current document index
 * @returns {number} - Current document index (default 0)
 */
export const getCurrentDocumentIndex = () => {
  const progress = loadFiscalProgress()
  return (progress && progress.currentDocumentIndex) || 0
}

/**
 * Get stored form data
 * @returns {Object|null} - Stored form data or null
 */
export const getStoredFormData = () => {
  const progress = loadFiscalProgress()
  return (progress && progress.formData) || null
}

export { clearFiscalProgress }

/**
 * Get progress summary for debugging
 * @returns {Object} - Summary of current progress
 */
export const getProgressSummary = () => {
  const progress = loadFiscalProgress()
  
  if (!progress) {
    return { hasProgress: false }
  }
  
  return {
    hasProgress: true,
    hasDocuments: !!(progress.documents && Object.keys(progress.documents).length > 0),
    documentsCount: progress.documents ? Object.keys(progress.documents).length : 0,
    acceptedCount: progress.acceptedDocuments ? progress.acceptedDocuments.length : 0,
    currentIndex: progress.currentDocumentIndex || 0,
    hasFormData: !!progress.formData,
    timestamp: progress.timestamp,
    age: progress.timestamp ? Date.now() - progress.timestamp : null
  }
}
